export type PhotoMetadataFormat = 'jpeg' | 'tiff' | 'heic'
export type MetadataAvailability = 'available' | 'not_found' | 'unsupported'

export interface MetadataValue {
  key: string
  label: string
  value: string | number | boolean
}

export interface MetadataGroup {
  id: 'camera' | 'date' | 'orientation' | 'gps' | 'serial' | 'software' | 'thumbnail' | 'other'
  values: MetadataValue[]
}

export interface MetadataInspection {
  format: PhotoMetadataFormat
  groups: MetadataGroup[]
  availability: {
    exif: MetadataAvailability
    iptc: MetadataAvailability
    xmp: MetadataAvailability
  }
  privacyFlags: Array<'gps' | 'serial' | 'software' | 'embedded_thumbnail'>
  json: Record<string, unknown>
}

export type MetadataInspectionErrorCode =
  | 'unsupported_input'
  | 'size_limit'
  | 'invalid_input'
  | 'processing_failed'

export class MetadataInspectionError extends Error {
  constructor(readonly code: MetadataInspectionErrorCode) {
    super(code)
    this.name = 'MetadataInspectionError'
  }
}

export const MAX_METADATA_FILE_BYTES = 50 * 1024 * 1024
export const MAX_METADATA_TAGS = 2_000
export const MAX_METADATA_DEPTH = 8
export const MAX_METADATA_STRING_LENGTH = 4_096
const MAX_CONTAINER_ITEMS = 4_096

const JPEG_SIGNATURE = [0xff, 0xd8, 0xff] as const
const TIFF_LE_SIGNATURE = [0x49, 0x49, 0x2a, 0x00] as const
const TIFF_BE_SIGNATURE = [0x4d, 0x4d, 0x00, 0x2a] as const
const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis'])

export function detectPhotoMetadataFormat(bytes: Uint8Array): PhotoMetadataFormat {
  if (startsWith(bytes, JPEG_SIGNATURE)) return 'jpeg'
  if (startsWith(bytes, TIFF_LE_SIGNATURE) || startsWith(bytes, TIFF_BE_SIGNATURE)) return 'tiff'
  if (bytes.length >= 8 && ascii(bytes, 4, 8) === 'ftyp') {
    if (bytes.length < 16) throw new MetadataInspectionError('invalid_input')
    const boxSize = readUint32(bytes, 0)
    if (boxSize < 16 || boxSize > bytes.length || boxSize > 4_096) {
      throw new MetadataInspectionError('invalid_input')
    }
    for (let offset = 8; offset + 4 <= boxSize; offset += 4) {
      if (HEIC_BRANDS.has(ascii(bytes, offset, offset + 4))) return 'heic'
    }
  }
  throw new MetadataInspectionError('unsupported_input')
}

export function validatePhotoMetadataContainer(
  bytes: Uint8Array,
  format: PhotoMetadataFormat,
): void {
  if (format === 'jpeg') validateJpeg(bytes)
  if (format === 'tiff') validateTiff(bytes)
  if (format === 'heic') validateIsoBmff(bytes)
}

export function normalizeMetadata(
  format: PhotoMetadataFormat,
  parsed: unknown,
  thumbnailFound: boolean,
): MetadataInspection {
  const source = isRecord(parsed) ? parsed : {}
  const bounded = boundMetadata(source)
  const values = flattenMetadata(bounded)
  const groups = metadataGroups(values, thumbnailFound)
  const hasExif = ['ifd0', 'exif', 'gps'].some((key) => {
    const block = source[key]
    return isRecord(block) && Object.keys(block).length > 0
  })
  const hasIptc = Object.prototype.hasOwnProperty.call(source, 'iptc')
  const hasXmp = Object.prototype.hasOwnProperty.call(source, 'xmp')
  const privacyFlags: MetadataInspection['privacyFlags'] = []
  if (groups.some((group) => group.id === 'gps')) privacyFlags.push('gps')
  if (groups.some((group) => group.id === 'serial')) privacyFlags.push('serial')
  if (groups.some((group) => group.id === 'software')) privacyFlags.push('software')
  if (thumbnailFound) privacyFlags.push('embedded_thumbnail')

  return {
    format,
    groups,
    availability: {
      exif: hasExif ? 'available' : 'not_found',
      iptc: format === 'heic' ? 'unsupported' : hasIptc ? 'available' : 'not_found',
      xmp: format === 'heic' ? 'unsupported' : hasXmp ? 'available' : 'not_found',
    },
    privacyFlags,
    json: bounded,
  }
}

function boundMetadata(value: Record<string, unknown>): Record<string, unknown> {
  let tags = 0
  const visit = (item: unknown, depth: number): unknown => {
    if (depth > MAX_METADATA_DEPTH) return '[depth limit]'
    if (item instanceof Date) return item.toISOString()
    if (typeof item === 'string') return item.slice(0, MAX_METADATA_STRING_LENGTH)
    if (typeof item === 'number') return Number.isFinite(item) ? item : String(item)
    if (typeof item === 'boolean' || item === null) return item
    if (ArrayBuffer.isView(item)) return `[binary ${item.byteLength} bytes]`
    if (Array.isArray(item)) return item.slice(0, 256).map((entry) => visit(entry, depth + 1))
    if (!isRecord(item)) return String(item)
    const output: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(item)) {
      tags += 1
      if (tags > MAX_METADATA_TAGS) throw new MetadataInspectionError('processing_failed')
      output[key.slice(0, 128)] = visit(entry, depth + 1)
    }
    return output
  }
  return visit(value, 0) as Record<string, unknown>
}

function flattenMetadata(source: Record<string, unknown>): MetadataValue[] {
  const values: MetadataValue[] = []
  const visit = (value: unknown, path: string[]): void => {
    if (Array.isArray(value) || isRecord(value)) {
      for (const [key, child] of Object.entries(value)) visit(child, [...path, key])
      return
    }
    if (value === '' || value === undefined || value === null) return
    const key = path.join('.')
    values.push({ key: key.toLowerCase(), label: path.at(-1) ?? key, value: displayValue(value) })
  }
  visit(source, [])
  return values
}

function metadataGroups(values: MetadataValue[], thumbnailFound: boolean): MetadataGroup[] {
  const result = new Map<MetadataGroup['id'], MetadataValue[]>()
  for (const value of values) {
    const id = groupForKey(value.key)
    result.set(id, [...(result.get(id) ?? []), value])
  }
  if (thumbnailFound) {
    result.set('thumbnail', [{ key: 'thumbnail', label: 'Embedded thumbnail', value: true }])
  }
  const order: MetadataGroup['id'][] = [
    'camera', 'date', 'orientation', 'gps', 'serial', 'software', 'thumbnail', 'other',
  ]
  return order.flatMap((id) => {
    const groupValues = result.get(id)
    return groupValues?.length ? [{ id, values: groupValues }] : []
  })
}

function groupForKey(key: string): MetadataGroup['id'] {
  if (/gps|latitude|longitude|altitude|location/.test(key)) return 'gps'
  if (/serial|ownername|artist|creator|author|byline|copyright/.test(key)) return 'serial'
  if (/software|firmware|processingsoftware|hostcomputer/.test(key)) return 'software'
  if (/orientation|rotation/.test(key)) return 'orientation'
  if (/date|time|created|modified/.test(key)) return 'date'
  if (/make|model|lens|camera|iso|fnumber|exposure|focal|flash|whitebalance/.test(key)) return 'camera'
  return 'other'
}

function displayValue(value: unknown): string | number | boolean {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  return JSON.stringify(value)
}

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
  return signature.every((value, index) => bytes[index] === value)
}

function ascii(bytes: Uint8Array, start: number, end: number): string {
  return String.fromCharCode(...bytes.subarray(start, end))
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return bytes[offset]! * 0x1000000
    + (bytes[offset + 1]! << 16)
    + (bytes[offset + 2]! << 8)
    + bytes[offset + 3]!
}

function validateJpeg(bytes: Uint8Array): void {
  let offset = 2
  let segments = 0
  while (offset < bytes.length) {
    if (++segments > MAX_CONTAINER_ITEMS || bytes[offset] !== 0xff) {
      throw new MetadataInspectionError('invalid_input')
    }
    while (bytes[offset] === 0xff) offset += 1
    const marker = bytes[offset++]
    if (marker === 0xd9) return
    if (marker === 0xda) {
      if (offset + 2 > bytes.length) throw new MetadataInspectionError('invalid_input')
      const length = (bytes[offset]! << 8) + bytes[offset + 1]!
      if (length < 2 || offset + length > bytes.length) throw new MetadataInspectionError('invalid_input')
      return
    }
    if (marker === undefined || marker === 0x00 || marker === 0xd8) {
      throw new MetadataInspectionError('invalid_input')
    }
    if (marker >= 0xd0 && marker <= 0xd7) continue
    if (offset + 2 > bytes.length) throw new MetadataInspectionError('invalid_input')
    const length = (bytes[offset]! << 8) + bytes[offset + 1]!
    if (length < 2 || offset + length > bytes.length) throw new MetadataInspectionError('invalid_input')
    offset += length
  }
  throw new MetadataInspectionError('invalid_input')
}

function validateTiff(bytes: Uint8Array): void {
  const littleEndian = bytes[0] === 0x49
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const read16 = (offset: number) => {
    if (offset < 0 || offset + 2 > bytes.length) throw new MetadataInspectionError('invalid_input')
    return view.getUint16(offset, littleEndian)
  }
  const read32 = (offset: number) => {
    if (offset < 0 || offset + 4 > bytes.length) throw new MetadataInspectionError('invalid_input')
    return view.getUint32(offset, littleEndian)
  }
  const typeSizes: Record<number, number> = {
    1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8,
  }
  const queue = [read32(4)]
  const visited = new Set<number>()
  let entries = 0
  while (queue.length) {
    const ifdOffset = queue.shift()!
    if (ifdOffset === 0 || visited.has(ifdOffset)) continue
    if (visited.size >= 128) throw new MetadataInspectionError('processing_failed')
    visited.add(ifdOffset)
    const count = read16(ifdOffset)
    entries += count
    if (entries > MAX_METADATA_TAGS) throw new MetadataInspectionError('processing_failed')
    const end = ifdOffset + 2 + count * 12
    if (!Number.isSafeInteger(end) || end + 4 > bytes.length) {
      throw new MetadataInspectionError('invalid_input')
    }
    for (let index = 0; index < count; index += 1) {
      const entry = ifdOffset + 2 + index * 12
      const tag = read16(entry)
      const type = read16(entry + 2)
      const valueCount = read32(entry + 4)
      const size = typeSizes[type]
      if (!size) throw new MetadataInspectionError('invalid_input')
      const valueBytes = valueCount * size
      if (!Number.isSafeInteger(valueBytes)) throw new MetadataInspectionError('invalid_input')
      if (valueBytes > 4) {
        const valueOffset = read32(entry + 8)
        if (valueOffset > bytes.length || valueBytes > bytes.length - valueOffset) {
          throw new MetadataInspectionError('invalid_input')
        }
      }
      if ((tag === 0x8769 || tag === 0x8825) && type === 4 && valueCount === 1) {
        queue.push(read32(entry + 8))
      }
    }
    queue.push(read32(end))
  }
}

function validateIsoBmff(bytes: Uint8Array): void {
  let offset = 0
  let boxes = 0
  while (offset < bytes.length) {
    if (++boxes > MAX_CONTAINER_ITEMS || offset + 8 > bytes.length) {
      throw new MetadataInspectionError('invalid_input')
    }
    let size = readUint32(bytes, offset)
    let header = 8
    if (size === 1) {
      if (offset + 16 > bytes.length) throw new MetadataInspectionError('invalid_input')
      const high = readUint32(bytes, offset + 8)
      const low = readUint32(bytes, offset + 12)
      size = high * 0x100000000 + low
      header = 16
    } else if (size === 0) {
      size = bytes.length - offset
    }
    if (!Number.isSafeInteger(size) || size < header || size > bytes.length - offset) {
      throw new MetadataInspectionError('invalid_input')
    }
    offset += size
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
